import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  MapPin,
  Calendar,
  Package,
  Clock,
  MessageSquare,
  Edit,
  X,
  XCircle,
  History,
  AlertCircle,
  Star,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  DataTable,
  rfqTableConfig,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@b2b/ui-components";
import { rfqService, authService } from "@b2b/services";
import { RfqDetail as RfqDetailType } from "@b2b/services/api/modules/rfq/rfq.types";
import { RFQMessageThread } from "@b2b/ui-components/features/RFQ/components/RFQMessageThread";
import { RFQActivityTimeline } from "@b2b/ui-components/features/RFQ/components/RFQActivityTimeline";
import { QuoteRatingDialog } from "@b2b/ui-components/features/RFQ/components/QuoteRatingDialog";
import { QuoteRevisionHistory } from "@b2b/ui-components/features/RFQ/components/QuoteRevisionHistory";
import { RFQDisputeDialog } from "@b2b/ui-components/features/RFQ/components/RFQDisputeDialog";

interface ToastState {
  type: "success" | "error";
  message: string;
}

export default function RFQDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // State
  const [rfqData, setRfqData] = useState<RfqDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [toast, setToast] = useState<ToastState | null>(null);
  
  // Action states
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [ratingQuote, setRatingQuote] = useState(false);
  
  // Dialog states
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [selectedQuoteForRating, setSelectedQuoteForRating] = useState<{ id: string; sellerName?: string } | null>(null);
  const [selectedQuoteForRevision, setSelectedQuoteForRevision] = useState<{ id: string } | null>(null);
  const [selectedQuoteForDispute, setSelectedQuoteForDispute] = useState<string | undefined>(undefined);
  const [disputeViewMode, setDisputeViewMode] = useState<"create" | "view">("create");
  
  // Refs to prevent loops
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);

  // Toast helper
  const showToast = useCallback((type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Extract RFQ data from response (handles double-wrapped structure)
  const extractRfqData = useCallback((response: any): RfqDetailType | null => {
    if (!response) return null;

    // Service returns { data: RfqDetail } or RfqDetail directly
    let data = response.data || response;

    // If still wrapped, unwrap one more level
    if (data && typeof data === 'object' && 'data' in data) {
      data = data.data;
    }

    // Verify it's an RFQ object
    if (data && typeof data === 'object' && (data.id || data.buyerId || data.title)) {
      return data as RfqDetailType;
    }

    return null;
  }, []);

  // Fetch RFQ data
  const fetchRfq = useCallback(async (rfqId: string) => {
    if (fetchingRef.current) return;
    
    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      const response = await rfqService.getRfqDetail(rfqId);
      const rfq = extractRfqData(response);
      
      if (!mountedRef.current) return;
      
      if (rfq) {
        setRfqData(rfq);
      } else {
        setError("RFQ not found");
        showToast("error", "RFQ not found or invalid response format");
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      
      const errorMessage = err?.response?.data?.message || "Failed to load RFQ";
      setError(errorMessage);
      showToast("error", errorMessage);
      
      if (err?.response?.status === 404) {
        setTimeout(() => navigate("/buyer/rfq_list"), 2000);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        fetchingRef.current = false;
      }
    }
  }, [extractRfqData, navigate, showToast]);

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await authService.getMe();
        if (response.user?.id) {
          setCurrentUserId(response.user.id);
        }
      } catch (err) {
        console.error("Error fetching current user:", err);
      }
    };
    fetchUser();
  }, []);

  // Fetch RFQ when id changes
  useEffect(() => {
    if (!id) {
      setRfqData(null);
      setLoading(false);
      return;
    }

    mountedRef.current = true;
    fetchRfq(id);

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Quote actions
  const handleAcceptQuote = useCallback(async (quoteId: string) => {
    if (!id) return;
    
    try {
      setAccepting(true);
      await rfqService.acceptQuote(id, quoteId);
      showToast("success", "Quote accepted successfully!");
      await fetchRfq(id);
    } catch (err: any) {
      showToast("error", err?.response?.data?.message || "Failed to accept quote");
    } finally {
      setAccepting(false);
    }
  }, [id, fetchRfq, showToast]);

  const handleRejectQuote = useCallback(async (quoteId: string) => {
    if (!id) return;
    
    try {
      setRejecting(quoteId);
      await rfqService.rejectQuote(id, quoteId);
      showToast("success", "Quote rejected successfully!");
      await fetchRfq(id);
    } catch (err: any) {
      showToast("error", err?.response?.data?.message || "Failed to reject quote");
    } finally {
      setRejecting(null);
    }
  }, [id, fetchRfq, showToast]);

  const handleRateQuote = useCallback(async (rating: number) => {
    if (!id || !selectedQuoteForRating) return;
    
    try {
      setRatingQuote(true);
      await rfqService.rateQuote(id, selectedQuoteForRating.id, rating);
      showToast("success", "Quote rated successfully!");
      await fetchRfq(id);
      setRatingDialogOpen(false);
    } catch (err: any) {
      showToast("error", err?.response?.data?.message || "Failed to rate quote");
    } finally {
      setRatingQuote(false);
    }
  }, [id, selectedQuoteForRating, fetchRfq, showToast]);

  const handleWithdrawRfq = useCallback(async () => {
    if (!id) return;
    
    try {
      setWithdrawing(true);
      await rfqService.withdrawRfq(id);
      showToast("success", "RFQ withdrawn successfully");
      navigate("/buyer/rfq_list");
    } catch (err: any) {
      showToast("error", err?.response?.data?.message || "Failed to withdraw RFQ");
    } finally {
      setWithdrawing(false);
      setWithdrawConfirmOpen(false);
    }
  }, [id, navigate, showToast]);

  const handleWithdrawQuote = useCallback(async (quoteId: string) => {
    if (!id) return;
    
    try {
      await rfqService.withdrawQuote(id, quoteId);
      showToast("success", "Quote withdrawn successfully");
      await fetchRfq(id);
    } catch (err: any) {
      showToast("error", err?.response?.data?.message || "Failed to withdraw quote");
    }
  }, [id, fetchRfq, showToast]);

  // Memoized callbacks for child components
  const handleMessageSent = useCallback(() => {
    if (id) fetchRfq(id);
  }, [id, fetchRfq]);

  const handleDisputeCreated = useCallback(() => {
    if (id) fetchRfq(id);
  }, [id, fetchRfq]);

  // Helper functions
  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "OPEN":
        return "text-white bg-green-600";
      case "CLOSED":
        return "bg-muted text-muted-foreground";
      case "CANCELLED":
        return "bg-red-600 text-white";
      case "DRAFT":
        return "bg-yellow-600 text-white";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-gray-500">Loading RFQ details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !rfqData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-500 mb-4">{error || "RFQ not found"}</p>
          <Button onClick={() => navigate("/buyer/rfq_list")} variant="outline">
            Back to RFQ List
          </Button>
        </div>
      </div>
    );
  }

  // Render quotes table data
  const quotesTableData = (rfqData.quotes || []).map((q) => ({
    ...q,
    supplier: (
      <div>
        <div className="font-medium">
          {q.seller?.company?.name || q.seller?.firstName || "N/A"}
        </div>
        <div className="text-sm text-muted-foreground">{q.seller?.email}</div>
      </div>
    ),
    price: `${q.currency} ${q.price.toLocaleString()}`,
    leadTime: `${q.leadTimeDays} days`,
    status: q.status,
    rating: q.rating ? (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= (q.rating || 0)
                ? "text-yellow-400 fill-yellow-400"
                : "text-gray-300"
            }`}
          />
        ))}
        <span className="text-sm text-muted-foreground ml-1">({q.rating}/5)</span>
      </div>
    ) : (
      <span className="text-sm text-muted-foreground">Not rated</span>
    ),
    actions: (
      <div className="flex gap-2 flex-wrap">
        {rfqData.status === "OPEN" && q.status === "SUBMITTED" && (
          <>
            <Button
              size="sm"
              variant="primary"
              onClick={() => handleAcceptQuote(q.id)}
              disabled={accepting || rejecting === q.id}
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleRejectQuote(q.id)}
              disabled={accepting || rejecting === q.id}
            >
              {rejecting === q.id ? "Rejecting..." : "Reject"}
            </Button>
          </>
        )}
        {q.status === "ACCEPTED" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedQuoteForRating({
                id: q.id,
                sellerName: q.seller?.company?.name || q.seller?.firstName,
              });
              setRatingDialogOpen(true);
            }}
            disabled={ratingQuote}
          >
            <Star className="w-3 h-3 mr-1" />
            {q.rating ? "Update Rating" : "Rate"}
          </Button>
        )}
        {(q.status === "SUBMITTED" || q.status === "ACCEPTED") && (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedQuoteForRevision({ id: q.id });
                setRevisionDialogOpen(true);
              }}
              title="View revision history"
            >
              <History className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedQuoteForDispute(q.id);
                setDisputeViewMode("create");
                setDisputeDialogOpen(true);
              }}
              title="Raise dispute"
            >
              <AlertCircle className="w-3 h-3" />
            </Button>
          </>
        )}
        {q.status === "SUBMITTED" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleWithdrawQuote(q.id)}
          >
            Withdraw
          </Button>
        )}
      </div>
    ),
  }));

  return (
    <div className="min-h-screen bg-background">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 p-4 rounded-lg shadow-lg max-w-[400px] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <p className="flex-1 font-medium text-sm">{toast.message}</p>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <main className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
            <div className="flex-1 mb-4 lg:mb-0">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {rfqData.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {rfqData.buyer?.company?.name || "N/A"}
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Posted {new Date(rfqData.createdAt).toLocaleDateString()}
                </div>
                <div className="flex items-center">
                  <Package className="h-4 w-4 mr-1" />
                  {rfqData.quantity} {rfqData.unit}
                </div>
                {rfqData.preferredDeliveryDate && (
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    Due {new Date(rfqData.preferredDeliveryDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            <Badge className={getStatusColor(rfqData.status)}>
              {rfqData.status}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* RFQ Overview */}
            <Card>
              <CardHeader>
                <CardTitle>RFQ Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-card-foreground mb-4">{rfqData.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Quantity:</span>
                    <span className="font-medium text-card-foreground">
                      {rfqData.quantity} {rfqData.unit}
                    </span>
                  </div>
                  {rfqData.preferredDeliveryDate && (
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Delivery Date:</span>
                      <span className="font-medium text-card-foreground">
                        {new Date(rfqData.preferredDeliveryDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {rfqData.categories && rfqData.categories.length > 0 && (
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Categories:</span>
                      <span className="font-medium text-card-foreground">
                        {rfqData.categories.map((c) => c.name).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quotes Received */}
            <Card>
              <CardHeader>
                <CardTitle>Quotes Received ({rfqData.quotes?.length || 0})</CardTitle>
              </CardHeader>
              <CardContent>
                {rfqData.quotes && rfqData.quotes.length > 0 ? (
                  <DataTable
                    data={quotesTableData}
                    config={rfqTableConfig}
                    rowsPerPage={5}
                  />
                ) : (
                  <p className="text-muted-foreground">No quotes received yet</p>
                )}
              </CardContent>
            </Card>

            {/* Messages Thread */}
            {currentUserId && id && (
              <RFQMessageThread
                rfqId={id}
                currentUserId={currentUserId}
                onMessageSent={handleMessageSent}
              />
            )}

            {/* Disputes Section */}
            {rfqData.disputes && rfqData.disputes.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Disputes ({rfqData.disputes.length})</CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setDisputeViewMode("view");
                      setDisputeDialogOpen(true);
                    }}
                  >
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {rfqData.disputes.slice(0, 3).map((dispute: any) => (
                      <div key={dispute.id} className="border rounded p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{dispute.reason}</span>
                          <Badge
                            className={
                              dispute.status === "PENDING"
                                ? "bg-yellow-100 text-yellow-800"
                                : dispute.status === "RESOLVED"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }
                          >
                            {dispute.status}
                          </Badge>
                        </div>
                        {dispute.description && (
                          <p className="text-muted-foreground text-xs mt-1">
                            {dispute.description.substring(0, 100)}
                            {dispute.description.length > 100 ? "..." : ""}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Activity Timeline */}
            {id && <RFQActivityTimeline rfqId={id} />}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Buyer Profile */}
            <Card>
              <CardHeader>
                <CardTitle>Buyer Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company:</span>
                  <span className="font-medium text-card-foreground">
                    {rfqData.buyer?.company?.name || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium text-card-foreground">
                    {rfqData.buyer?.email || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium text-card-foreground">
                    {rfqData.buyer?.firstName} {rfqData.buyer?.lastName}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {rfqData.status === "OPEN" && (
                  <Button asChild className="w-full" size="lg" variant="primary">
                    <Link to={`/seller/rfq/${id}/quote`}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Submit Quote
                    </Link>
                  </Button>
                )}
                {rfqData.status === "DRAFT" && currentUserId === rfqData.buyerId && (
                  <Button variant="outline" className="w-full" asChild>
                    <Link to={`/buyer/rfq/${id}/edit`}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit RFQ
                    </Link>
                  </Button>
                )}
                {rfqData.status === "OPEN" && currentUserId === rfqData.buyerId && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setWithdrawConfirmOpen(true)}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Withdraw RFQ
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Dialogs */}
      {selectedQuoteForRating && (
        <QuoteRatingDialog
          open={ratingDialogOpen}
          onOpenChange={setRatingDialogOpen}
          onRate={handleRateQuote}
          quoteId={selectedQuoteForRating.id}
          sellerName={selectedQuoteForRating.sellerName}
        />
      )}

      {selectedQuoteForRevision && id && (
        <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Quote Revision History</DialogTitle>
            </DialogHeader>
            <QuoteRevisionHistory rfqId={id} quoteId={selectedQuoteForRevision.id} />
          </DialogContent>
        </Dialog>
      )}

      {id && (
        <RFQDisputeDialog
          open={disputeDialogOpen}
          onOpenChange={setDisputeDialogOpen}
          rfqId={id}
          quoteId={selectedQuoteForDispute}
          mode={disputeViewMode}
          onDisputeCreated={handleDisputeCreated}
        />
      )}

      <AlertDialog open={withdrawConfirmOpen} onOpenChange={setWithdrawConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw RFQ</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to withdraw this RFQ? This action cannot be undone and all sellers will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleWithdrawRfq}
              disabled={withdrawing}
              className="bg-red-600 hover:bg-red-700"
            >
              {withdrawing ? "Withdrawing..." : "Withdraw"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
